import React, { useState, useEffect } from 'react';
import { useApp } from '../core/context';
import { uuid } from '../core/helpers';
import { ORG_JSON_KEYS, persistOrgJsonSetting } from '../core/org-json-settings';
import { Icon, InlineIcon } from './Icon';

const ALL_WIDGETS = [
  { id: 'clients', label: 'Active Clients' },
  { id: 'billing', label: 'Total Billing' },
  { id: 'collected', label: 'Collected' },
  { id: 'pending', label: 'Outstanding' },
  { id: 'pending_tasks', label: 'Pending Tasks' },
  { id: 'overdue_tasks', label: 'Overdue Tasks' },
  { id: 'completed_tasks', label: 'Completed Tasks' },
  { id: 'employees', label: 'Team Members' },
  { id: 'team_panel', label: 'Tasks & Team Panel' },
  { id: 'emp_overview', label: 'Employee Overview Table' },
  { id: 'tasks_by_employee', label: 'Tasks by Employee Matrix' },
  { id: 'ticket_summary', label: 'Tickets by Employee' },
  { id: 'client_overview', label: 'Client Overview Table' },
];

const DEFAULT_WIDGETS = ALL_WIDGETS.map(w => w.id);

function sanitizeWidgets(ids) {
  const allowed = new Set(ALL_WIDGETS.map(w => w.id));
  const clean = (ids || []).filter(id => allowed.has(id));
  return clean.length ? clean : DEFAULT_WIDGETS;
}

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${breakpoint}px)`).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const fn = () => setMobile(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [breakpoint]);
  return mobile;
}

function SavedDashboardBar({
  period, customFrom, customTo, setPeriod, setCustomFrom, setCustomTo,
  activeWidgets, setActiveWidgets, showTaskPanel, setShowTaskPanel,
}) {
  const { user, org, setOrg, showToast } = useApp();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const dashboards = Array.isArray(org?.savedDashboards) ? org.savedDashboards : [];
  const [activeId, setActiveId] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [title, setTitle] = useState('');
  const isMobile = useIsMobile();

  const mine = dashboards.filter(d => !d.createdById || d.createdById === user?.id || isAdmin);

  const applyDashboard = (d) => {
    if (!d) {
      setActiveId('');
      setActiveWidgets(DEFAULT_WIDGETS);
      setShowTaskPanel(true);
      return;
    }
    setActiveId(d.id);
    setPeriod(d.period || 'month');
    setCustomFrom(d.customFrom || '');
    setCustomTo(d.customTo || '');
    if (Array.isArray(d.widgets) && d.widgets.length) setActiveWidgets(sanitizeWidgets(d.widgets));
    if (typeof d.showTaskPanel === 'boolean') setShowTaskPanel(d.showTaskPanel);
  };

  const saveDashboard = async () => {
    if (!title.trim()) { showToast('Dashboard name required', 'error'); return; }
    const item = {
      id: 'dash_' + uuid().slice(0, 8),
      title: title.trim(),
      createdById: user?.id,
      createdBy: user?.name || 'Admin',
      period, customFrom, customTo,
      widgets: activeWidgets,
      showTaskPanel,
      createdAt: new Date().toISOString(),
    };
    const next = [...dashboards, item];
    try {
      await persistOrgJsonSetting(ORG_JSON_KEYS.savedDashboards, next, setOrg, 'savedDashboards');
      setActiveId(item.id);
      setTitle('');
      setShowSave(false);
      showToast(`Dashboard "${item.title}" saved`, 'success');
    } catch {
      showToast('Could not save dashboard', 'error');
    }
  };

  const removeDashboard = async (id) => {
    const next = dashboards.filter(d => d.id !== id);
    await persistOrgJsonSetting(ORG_JSON_KEYS.savedDashboards, next, setOrg, 'savedDashboards');
    if (activeId === id) setActiveId('');
    showToast('Dashboard removed', 'success');
  };

  const toggleWidget = (id) => {
    setActiveWidgets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const topicsSelect = (
    <select
      className="f-select saved-dashboard-select saved-dashboard-topics-select"
      value=""
      onChange={(e) => {
        const v = e.target.value;
        if (!v) return;
        toggleWidget(v);
        e.target.value = '';
      }}
    >
      <option value="">
        Topics ({activeWidgets.length}/{ALL_WIDGETS.length} selected)
      </option>
      {ALL_WIDGETS.map(w => (
        <option key={w.id} value={w.id}>
          {activeWidgets.includes(w.id) ? '✓ ' : ''}{w.label}
        </option>
      ))}
    </select>
  );

  if (!isAdmin) return null;

  return (
    <div className="saved-dashboard-bar">
      {isMobile ? (
        <>
          <div className="saved-dashboard-mobile-row">
            <label className="saved-dashboard-mobile-lbl">My dashboard</label>
            <select
              className="f-select saved-dashboard-select"
              value={activeId || 'default'}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'default') applyDashboard(null);
                else applyDashboard(mine.find(d => d.id === v));
              }}
            >
              <option value="default">Default layout</option>
              {mine.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
          <div className="saved-dashboard-mobile-row">
            <label className="saved-dashboard-mobile-lbl">Topics shown</label>
            {topicsSelect}
          </div>
          <div className="saved-dashboard-mobile-actions">
            <label className="f-check saved-dashboard-task-toggle">
              <input type="checkbox" checked={showTaskPanel} onChange={e => setShowTaskPanel(e.target.checked)} />
              Task panel
            </label>
            <button type="button" className="btn btn-sm btn-primary saved-dashboard-save-btn" onClick={() => setShowSave(true)}>
              <InlineIcon name="add" size={13} gap={4}>Save</InlineIcon>
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>My dashboards:</span>
            <button type="button" className={`chip ${!activeId ? 'on' : ''}`} onClick={() => applyDashboard(null)}>Default</button>
            {mine.map(d => (
              <span key={d.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <button type="button" className={`chip ${activeId === d.id ? 'on' : ''}`} onClick={() => applyDashboard(d)}>{d.title}</button>
                <button type="button" onClick={() => removeDashboard(d.id)} style={{ border: 'none', background: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 11 }}>×</button>
              </span>
            ))}
            <button type="button" className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setShowSave(true)}>
              <InlineIcon name="add" size={13} gap={4}>Save current layout</InlineIcon>
            </button>
          </div>
          <div className="saved-dashboard-topics-row">
            <span className="saved-dashboard-topics-lbl">Topics:</span>
            {topicsSelect}
            <label className="f-check saved-dashboard-task-toggle">
              <input type="checkbox" checked={showTaskPanel} onChange={e => setShowTaskPanel(e.target.checked)} />
              Task panel
            </label>
          </div>
        </>
      )}
      <div className="saved-dashboard-save-row">
        {!isMobile && showSave && (
          <>
            <input className="f-input saved-dashboard-save-input" placeholder="Dashboard name…" value={title} onChange={e => setTitle(e.target.value)} />
            <button type="button" className="btn btn-sm btn-primary" onClick={saveDashboard}>Save</button>
            <button type="button" className="btn btn-sm" onClick={() => setShowSave(false)}>Cancel</button>
          </>
        )}
        {isMobile && showSave && (
          <>
            <input className="f-input" placeholder="Dashboard name…" value={title} onChange={e => setTitle(e.target.value)} />
            <div className="saved-dashboard-save-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={saveDashboard}>Save</button>
              <button type="button" className="btn btn-sm" onClick={() => setShowSave(false)}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { SavedDashboardBar, ALL_WIDGETS, DEFAULT_WIDGETS, sanitizeWidgets };
